import { useState } from "react";
import { X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@web/ui/Button";
import { MENU_ITEM_CLASS } from "@web/ui/Menu";
import { Modal } from "@web/ui/Modal";
import { UnsubscribeControl } from "./UnsubscribeControl";

/** Opening this dialog only reads cached list headers; the owner chooses any outward action. */
export function UnsubscribeButton({
  accountId,
  accountEmail,
  address,
  name,
  threadKey,
  onOpen,
  variant = "button",
}: {
  accountId: string;
  accountEmail: string;
  address: string;
  name?: string;
  threadKey: string;
  onOpen?: () => void;
  /** "menu" renders the trigger as a row of a Menu; the dialog and its confirm are the same. */
  variant?: "button" | "menu";
}) {
  const [open, setOpen] = useState(false);
  const openDialog = () => {
    onOpen?.();
    setOpen(true);
  };
  const label = `Unsubscribe from ${name || address}`;
  return (
    <>
      {variant === "menu" ? (
        <button type="button" className={MENU_ITEM_CLASS} onClick={openDialog} aria-label={label}>
          Unsubscribe…
        </button>
      ) : (
        <Button size="sm" variant="ghost" onClick={openDialog} aria-label={label}>
          Unsubscribe…
        </Button>
      )}
      <Modal open={open} onClose={() => setOpen(false)} label="Unsubscribe">
        <div className="flex w-[min(520px,90vw)] flex-col gap-4 p-5">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-2xl">Unsubscribe from {name || address}?</h2>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Close unsubscribe"
              onClick={() => setOpen(false)}
            >
              <X aria-hidden className="size-4" />
            </Button>
          </div>
          <p className="text-sm break-words">
            <strong>{name || address}</strong>
            {name ? (
              <>
                <br />
                {address}
              </>
            ) : null}
          </p>
          <p className="text-sm text-muted">Mailbox: {accountEmail}</p>
          <UnsubscribeControl
            accountId={accountId}
            address={address}
            showUnavailable
            confirmOnOpen
            onCancel={() => setOpen(false)}
            onDraftOpened={() => setOpen(false)}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Link
              to="/t/$key"
              params={{ key: threadKey }}
              onClick={() => setOpen(false)}
              className="text-sm text-accent-text underline underline-offset-2"
            >
              Read the email
            </Link>
          </div>
        </div>
      </Modal>
    </>
  );
}
